<template>
  <section class="custom-detail">
    <!-- Save and Resolve belong to the column they act on, so this header keeps only
         what leaves the view -->
    <div class="row custom-page-header">
      <div class="col">
        <button type="button" class="btn btn-light ms-2" @click="close()"><i class="fa fa-arrow-left me-1"></i>Back to List</button>
        <button type="button" class="btn btn-light ms-2" @click="close()">{{ state.isReadOnly ? 'Close' : 'Cancel' }}</button>
      </div>
    </div>
    <!-- the reported link on the left, what LaunchPad holds for the same vehicle on the right -->
    <div v-if="modelValue" ref="split" class="custom-split">
      <div class="custom-split-column" :style="leftStyle">
        <repair-link-adas-map-detail
          ref="detail"
          :model-value="modelValue"
          @closing="$emit('closing')"
          @saved="$emit('saved', $event)"
          @update:model-value="$emit('update:modelValue', $event)"
          @state-change="onStateChange"
        />
      </div>
      <!-- drag (or arrow key) to move the split; double click puts it back to even -->
      <div
        class="custom-split-divider"
        :class="{ 'custom-split-divider-active': isDragging }"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize columns"
        :aria-valuenow="Math.round(leftWidth)"
        aria-valuemin="20"
        aria-valuemax="80"
        tabindex="0"
        title="Drag to resize"
        @mousedown="startResize"
        @touchstart="startResize"
        @dblclick="resetSplit"
        @keydown.left.prevent="nudge(-2)"
        @keydown.right.prevent="nudge(2)"
      ></div>
      <div class="custom-split-column custom-split-right">
        <repair-link-launch-pad-detail :model="modelValue" />
      </div>
    </div>
  </section>
</template>
<script>
import RepairLinkAdasMapDetail from '@/components/adasmatrix/repair-link-adas-map-detail.vue'
import RepairLinkLaunchPadDetail from '@/components/adasmatrix/repair-link-launch-pad-detail.vue'

/**
 * The repair view, side by side: the broken link as the ADAS Map application that
 * reported it holds it, against the LaunchPad matrix record for the same
 * vehicle. Each column saves itself -- the reported side through its own Save and
 * Resolve, the record through its own Save -- so the chrome here carries only
 * what leaves the view, off the left column's state.
 */
export default {
  name: 'RepairLinkDetail',
  components: {
    RepairLinkAdasMapDetail,
    RepairLinkLaunchPadDetail
  },
  props: {
    modelValue: Object
  },
  emits: ['closing', 'saved', 'update:modelValue'],
  data() {
    return {
      // width of the left column as a percentage; only applied while the columns sit side by side
      leftWidth: 50,
      isDragging: false,
      isWide: true,
      state: {
        hasChanges: false,
        isReadOnly: false,
        message: null,
        isHighlight: false,
        id: null,
        showActions: false
      }
    }
  },
  computed: {
    /** Stacked (below lg) the columns are full width, so the split only applies side by side. */
    leftStyle() {
      if (!this.isWide) {
        return null
      }
      return { flex: `0 0 ${this.leftWidth}%`, maxWidth: `${this.leftWidth}%` }
    }
  },
  mounted() {
    // matches the breakpoint the stacked / side by side layout switches at below
    this.wideQuery = window.matchMedia('(min-width: 992px)')
    this.isWide = this.wideQuery.matches
    this.onWideChange = event => {
      this.isWide = event.matches
    }
    this.wideQuery.addEventListener('change', this.onWideChange)
  },
  beforeUnmount() {
    if (this.wideQuery) {
      this.wideQuery.removeEventListener('change', this.onWideChange)
    }
    this.stopResize()
  },
  methods: {
    startResize(event) {
      if (!this.isWide) {
        return
      }
      event.preventDefault()
      this.isDragging = true
      document.addEventListener('mousemove', this.onResize)
      document.addEventListener('mouseup', this.stopResize)
      document.addEventListener('touchmove', this.onResize, { passive: false })
      document.addEventListener('touchend', this.stopResize)
      // keep the resize cursor and stop the drag from selecting the text it passes over
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    onResize(event) {
      const container = this.$refs.split
      if (!container) {
        return
      }
      if (event.cancelable) {
        event.preventDefault()
      }
      const point = event.touches ? event.touches[0] : event
      const bounds = container.getBoundingClientRect()
      if (!bounds.width) {
        return
      }
      this.setLeftWidth(((point.clientX - bounds.left) / bounds.width) * 100)
    },
    stopResize() {
      if (!this.isDragging) {
        return
      }
      this.isDragging = false
      document.removeEventListener('mousemove', this.onResize)
      document.removeEventListener('mouseup', this.stopResize)
      document.removeEventListener('touchmove', this.onResize)
      document.removeEventListener('touchend', this.stopResize)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    },
    /** Neither side is allowed to collapse far enough to become unusable. */
    setLeftWidth(percent) {
      this.leftWidth = Math.min(80, Math.max(20, percent))
    },
    nudge(step) {
      if (this.isWide) {
        this.setLeftWidth(this.leftWidth + step)
      }
    },
    resetSplit() {
      this.leftWidth = 50
    },
    onStateChange(state) {
      this.state = state
    },
    /** The left column owns the unsaved-changes prompt, so leaving the view goes through it. */
    close() {
      if (this.$refs.detail) {
        this.$refs.detail.requestClose()
        return
      }
      this.$emit('closing')
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-detail {
  width: 100%;
}
.custom-page-header {
  align-items: center;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}
.custom-right-align {
  text-align: right;
}
.custom-split {
  display: flex;
  flex-direction: column;
  margin: 0;
}
.custom-split-column {
  padding: 0 0.75rem;
  min-width: 0; // without this the columns refuse to shrink past their content
}
.custom-split-divider {
  display: none;
}
// the divider only makes sense while the two columns actually sit side by side
@media (min-width: 992px) {
  .custom-split {
    flex-direction: row;
    align-items: stretch;
  }
  .custom-split-column {
    flex: 1 1 0;
  }
  .custom-split-divider {
    display: block;
    position: relative;
    flex: 0 0 auto;
    // a thin line to look at, a wider strip to actually grab
    width: 9px;
    margin: 0 -4px;
    cursor: col-resize;
    touch-action: none;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 50%;
      width: 1px;
      transform: translateX(-50%);
      background: #dee2e6;
    }

    &:hover::before,
    &:focus-visible::before,
    &.custom-split-divider-active::before {
      width: 3px;
      background: #0d6efd;
    }

    &:focus {
      outline: none;
    }
  }
}
</style>
